import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import logo from '../assets/theProposal-logo.png';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [token]);

  useEffect(() => {
    setPasswordValidation({
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /\d/.test(newPassword)
    });
  }, [newPassword]);

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);
  const doPasswordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid or missing reset token.');
      return;
    }

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements.');
      return;
    }

    if (!doPasswordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await api.post('/auth/customer/reset-password', {
        token,
        newPassword
      });
      setIsSubmitted(true);
    } catch (err: any) {
      if (err.response?.data?.code === 'AUTH_006') {
        setError('This reset link is invalid or has expired. Please request a new password reset.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <h2 className="mt-4 text-xl font-semibold text-gray-900">
                Password Reset Successfully!
              </h2>
              <p className="mt-2 text-sm text-gray-600 mb-6">
                Your password has been updated. You can now log in with your new password.
              </p>
              <Link
                to="/login"
                className="btn-primary w-full"
              >
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <img 
            src={logo} 
            alt="The Proposal Bridal" 
            className="h-16 mx-auto"
          />
          <h2 className="mt-6 text-2xl font-semibold text-gray-900">
            Set New Password
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Please create a new password for your account.
          </p>
        </div>

        <div className="mt-8">
          <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
            {error && error.includes('invalid') || error.includes('expired') ? (
              <div className="text-center">
                <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
                <h2 className="mt-4 text-xl font-semibold text-gray-900">
                  Invalid Reset Link
                </h2>
                <p className="mt-2 text-sm text-gray-600 mb-6">
                  {error}
                </p>
                <div className="space-y-3">
                  <Link
                    to="/forgot-password"
                    className="btn-primary w-full"
                  >
                    Request New Reset Link
                  </Link>
                  <Link
                    to="/login"
                    className="btn-secondary w-full"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="newPassword"
                      name="newPassword"
                      type="password"
                      required
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Enter new password"
                    />
                  </div>
                  
                  {newPassword && (
                    <div className="mt-2 space-y-1">
                      <div className="text-xs text-gray-600">Password requirements:</div>
                      <div className={`text-xs flex items-center ${passwordValidation.length ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-2">{passwordValidation.length ? '✓' : '○'}</span>
                        At least 8 characters
                      </div>
                      <div className={`text-xs flex items-center ${passwordValidation.uppercase ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-2">{passwordValidation.uppercase ? '✓' : '○'}</span>
                        One uppercase letter
                      </div>
                      <div className={`text-xs flex items-center ${passwordValidation.lowercase ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-2">{passwordValidation.lowercase ? '✓' : '○'}</span>
                        One lowercase letter
                      </div>
                      <div className={`text-xs flex items-center ${passwordValidation.number ? 'text-green-600' : 'text-gray-400'}`}>
                        <span className="mr-2">{passwordValidation.number ? '✓' : '○'}</span>
                        One number
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Confirm new password"
                    />
                  </div>
                  {confirmPassword && (
                    <div className={`mt-1 text-xs flex items-center ${doPasswordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                      <span className="mr-2">{doPasswordsMatch ? '✓' : '✗'}</span>
                      {doPasswordsMatch ? 'Passwords match' : 'Passwords do not match'}
                    </div>
                  )}
                </div>

                {error && !error.includes('invalid') && !error.includes('expired') && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={isLoading || !isPasswordValid || !doPasswordsMatch || !token}
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Updating Password...
                      </div>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-primary-600 hover:text-primary-500"
                  >
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;